if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.input = this.querySelector('[data-js-search-input]');
      this.panel = this.querySelector('[data-js-search-results]');
      this.form = this.querySelector('form');
      if (!this.input || !this.panel || !this.form) return;
      this.initialized = true;
      document.body.classList.add('predictive-script-loaded');
      this.cache = new Map();
      this.sequence = 0;
      this.activeIndex = -1;
      this.items = [];
      this.input.setAttribute('aria-expanded', 'false');
      this.onInput = () => {
        clearTimeout(this.timer);
        this.controller?.abort();
        this.sequence++;
        this.resetSelection();
        this.items = [];
        const query = this.input.value.trim();
        if (!query) {
          this.panel.replaceChildren();
          this.close();
          return;
        }
        this.timer = setTimeout(() => this.getSearchResults(query), 180);
      };
      this.onFocus = () => {
        if (!this.input.value.trim()) return;
        if (this.panel.childElementCount && this.renderedQuery === this.input.value.trim()) {
          this.open();
        } else {
          this.onInput();
        }
      };
      this.onKeydown = event => {
        if (event.isComposing) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          clearTimeout(this.timer);
          this.controller?.abort();
          this.sequence++;
          this.close();
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          if (!this.items.length) return;
          event.preventDefault();
          this.open();
          this.navigateThrough(event.key === 'ArrowDown' ? 1 : -1);
        } else if (event.key === 'Enter' && this.activeIndex >= 0) {
          event.preventDefault();
          this.items[this.activeIndex].click();
        }
      };
      this.onOutside = event => {
        if (!this.contains(event.target)) this.close();
      };
      this.onFocusout = event => {
        if (!this.contains(event.relatedTarget)) this.close();
      };
      this.onResize = () => {
        if (this.classList.contains('bps-search-open')) this.positionPanel();
      };
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusout);
      document.addEventListener('pointerdown', this.onOutside);
      window.addEventListener('resize', this.onResize);
      window.visualViewport?.addEventListener('resize', this.onResize);
      if (document.activeElement === this.input && this.input.value.trim()) this.onInput();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('focus', this.onFocus);
      this.input?.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusout);
      document.removeEventListener('pointerdown', this.onOutside);
      window.removeEventListener('resize', this.onResize);
      window.visualViewport?.removeEventListener('resize', this.onResize);
      this.initialized = false;
    }

    async getSearchResults(query) {
      const sequence = ++this.sequence;
      if (this.cache.has(query.toLowerCase())) {
        this.renderSearchResults(this.cache.get(query.toLowerCase()), query);
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      this.panel.setAttribute('aria-busy', 'true');
      this.panel.innerHTML = KROWN.settings.predictive_search_placeholder;
      this.querySelector('[data-bps-search-status]').textContent = this.dataset.loading;
      this.open();
      try {
        const url = new URL(KROWN.settings.routes.predictive_search_url, location.origin);
        url.search = new URLSearchParams({
          q: query,
          section_id: 'helper-predictive-search',
          'resources[type]': 'product,query,collection,page,article',
          'resources[limit]': '4',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'last'
        }).toString();
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        let section = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('#shopify-section-helper-predictive-search');
        if (!section) throw new Error('Search unavailable');
        const partNumber = query.match(/^([a-z]{1,5})(\d{2,})([a-z]?)$/i);
        if (!section.querySelector('[data-bps-search-product]') && partNumber) {
          url.searchParams.set('q', partNumber[1] + '-' + partNumber[2] + partNumber[3]);
          try {
            const alternateResponse = await fetch(url, { signal: this.controller.signal });
            if (alternateResponse.ok) {
              const alternateHtml = await alternateResponse.text();
              const alternateSection = new DOMParser().parseFromString(alternateHtml, 'text/html')
                .querySelector('#shopify-section-helper-predictive-search');
              if (alternateSection?.querySelector('[data-bps-search-product]')) section = alternateSection;
            }
          } catch (error) {
            if (error.name === 'AbortError') throw error;
          }
        }
        if (sequence !== this.sequence || query !== this.input.value.trim()) return;
        if (this.cache.size >= 30) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query.toLowerCase(), section.innerHTML);
        this.renderSearchResults(section.innerHTML, query);
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) this.renderFallback(query);
      }
    }

    renderSearchResults(html, query) {
      this.panel.innerHTML = html;
      this.renderedQuery = query;
      this.panel.setAttribute('aria-busy', 'false');
      this.items = Array.from(this.panel.querySelectorAll('[data-js-search-item]'));
      this.items.forEach((item, index) => { item.id = this.panel.id + '-item-' + index; item.setAttribute('role', 'option'); });
      this.resetSelection();
      const count = this.panel.querySelectorAll('[data-bps-search-product]').length;
      const status = this.querySelector('[data-bps-search-status]');
      if (status) status.textContent = this.dataset.results.replace('[count]', count);
      if (this.contains(document.activeElement)) this.open();
    }

    renderFallback(query) {
      const card = document.createElement('div');
      card.className = 'bps-search-fallback';
      const message = document.createElement('p');
      message.textContent = this.dataset.error;
      const resultsLink = document.createElement('a');
      resultsLink.className = 'bps-search-all';
      resultsLink.dataset.jsSearchItem = '';
      const searchUrl = new URL(this.form.action);
      searchUrl.searchParams.set('q', query);
      resultsLink.href = searchUrl.href;
      resultsLink.textContent = this.dataset.all;
      const helpLink = document.createElement('a');
      helpLink.className = 'bps-search-help-link';
      helpLink.dataset.jsSearchItem = '';
      const helpUrl = new URL(this.dataset.helpUrl, location.origin);
      helpUrl.searchParams.set('product', query);
      helpLink.href = helpUrl.href;
      helpLink.textContent = this.dataset.help;
      card.append(message, resultsLink, helpLink);
      this.panel.replaceChildren(card);
      this.panel.setAttribute('aria-busy', 'false');
      this.items = [resultsLink, helpLink];
      this.items.forEach((item, index) => { item.id = this.panel.id + '-item-' + index; item.setAttribute('role', 'option'); });
      this.renderedQuery = query;
      this.resetSelection();
      this.querySelector('[data-bps-search-status]').textContent = this.dataset.error;
      if (this.contains(document.activeElement)) this.open();
    }

    positionPanel() {
      const rect = this.getBoundingClientRect();
      const width = Math.min(720, window.innerWidth - 32);
      const left = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)) - rect.left;
      const height = (window.visualViewport?.height || window.innerHeight) - rect.bottom - 20;
      this.panel.style.setProperty('--bps-search-width', width + 'px');
      this.panel.style.setProperty('--bps-search-left', left + 'px');
      this.panel.style.setProperty('--bps-search-height', Math.max(180, Math.min(620, height)) + 'px');
    }

    open() {
      this.classList.add('bps-search-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.positionPanel();
      const overlay = this.querySelector('.search-results-overlay');
      if (overlay) {
        overlay.style.display = '';
        overlay.classList.add('active');
      }
    }

    close() {
      this.classList.remove('bps-search-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-busy', 'false');
      this.querySelector('.search-results-overlay')?.classList.remove('active');
      this.resetSelection();
    }

    resetSelection() {
      this.items.forEach(item => { item.classList.remove('active'); item.setAttribute('aria-selected', 'false'); });
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    navigateThrough(direction) {
      if (this.activeIndex >= 0) { this.items[this.activeIndex].classList.remove('active'); this.items[this.activeIndex].setAttribute('aria-selected', 'false'); }
      this.activeIndex = Math.max(-1, Math.min(this.items.length - 1, this.activeIndex + direction));
      if (this.activeIndex < 0) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      const item = this.items[this.activeIndex];
      item.classList.add('active');
      item.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', item.id);
      item.scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('search-form', SearchForm);
}

